import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * Enhanced SEO Meta Tags component for Guilt Gauge
 * 
 * This component provides optimized meta tags for each view in the application
 * to improve search engine visibility and click-through rates.
 * 
 * @param {Object} props Component props
 * @param {string} props.currentView Current view/page being displayed
 * @param {Object} props.purchaseData Optional purchase data for results view
 * @param {Object} props.userProfile Optional user profile data
 */
const EnhancedSEOMeta = ({ currentView, purchaseData, userProfile }) => {
  // Base URL for the site
  const baseUrl = 'https://guiltgauge.com';
  
  // Function to get canonical URL
  const getCanonicalUrl = (currentView) => {
    const baseUrl = 'https://guiltgauge.com';
    
    // Mapping of views to canonical paths
    const viewToPath = {
      "landing": "/",
      "input": "/calculate",
      "results": "/results",
      "dashboard": "/dashboard",
      "insights": "/insights",
      "challenges": "/challenges",
      "goals": "/goals",
      "shop": "/shop",
      "blog": "/blog",
      "quiz-hub": "/quizzes",
      "shopaholic": "/shopping-addiction",
      "shopaholic-test": "/shopping-addiction-test",
      "guilt-after-shopping": "/why-feel-guilty-after-shopping",
      "fashion-guilt": "/fashion-shopping-guilt",
      "tech-guilt": "/tech-shopping-guilt",
      "luxury-guilt": "/luxury-shopping-guilt",
      "home-decor-remorse": "/home-decor-remorse",
      "holiday-guilt": "/holiday-shopping-guilt",
      "black-friday-remorse": "/black-friday-remorse",
      "birthday-splurge": "/birthday-splurge",
      "student-guilt": "/student-shopping-guilt",
      "parent-guilt": "/parent-shopping-guilt",
      "retirement-shopping": "/retirement-shopping",
      "debt-shopping": "/debt-shopping",
      "budget-shopping": "/budget-shopping",
      "post-payday": "/post-payday-shopping",
      "emotional-shopping": "/emotional-shopping-tracker",
      "impulse-buying": "/impulse-buying",
      "fomo-shopping": "/fomo-shopping",
      "late-night-shopping": "/late-night-shopping",
      "no-spend-challenge": "/no-spend-challenge",
      "shopping-detox": "/shopping-detox",
      "addiction-stories": "/shopping-addiction-stories",
      "guilt-vs-anxiety": "/shopping-guilt-vs-anxiety",
      "retail-therapy": "/retail-therapy-vs-addiction",
      "remorse-timeline": "/buyers-remorse-timeline",
      "buy-now-pay-later": "/buy-now-pay-later",
    };
    
    return `${baseUrl}${viewToPath[currentView] || '/'}`;
  };
  
  // Default meta data
  const defaultMeta = {
    title: 'Guilt Gauge | Calculate Your Shopping Guilt Score',
    description: 'Track your purchases, understand your shopping patterns, and get personalized advice to shop without regret. Free tool for mindful spending.',
    keywords: 'shopping guilt, guilt after shopping, buyer\'s remorse, retail therapy, shopping regret, emotional spending, mindful shopping',
    canonical: baseUrl,
    ogType: 'website',
    ogImage: `${baseUrl}/og-image.png`,
    twitterCard: 'summary_large_image'
  };
  
  // View-specific meta data optimized for SEO
  const viewMeta = {
    landing: {
      title: 'Stop Feeling Guilty After Shopping | Guilt Gauge Calculator',
      description: 'Calculate your shopping guilt score and get personalized advice to shop without regret. Free tool for mindful spending and overcoming buyer\'s remorse.',
      keywords: 'shopping guilt calculator, stop feeling guilty after shopping, guilt free shopping, overcome buyer\'s remorse',
      canonical: baseUrl,
    },
    shopaholic: {
      title: 'Shopping Addiction Self-Help | Free Tools For Compulsive Buyers',
      description: 'Overcome compulsive buying disorder with our free tools. Track your shopping habits, identify emotional triggers, and develop strategies to break the shopaholic cycle.',
      keywords: 'shopping addiction, shopaholic, compulsive buying disorder, shopping addiction help, shopaholic recovery, compulsive shopping, overcome shopping addiction, shopping addiction test',
      canonical: `${baseUrl}/shopping-addiction`,
    },
    input: {
      title: 'Calculate Your Shopping Guilt Score | Free Online Tool',
      description: 'Enter your purchase details to get an instant guilt score and personalized advice. Track impulse buys, emotional purchases, and shop smarter.',
      keywords: 'shopping guilt calculator, guilt score, purchase tracker, impulse buy tracker, shopping guilt',
      canonical: `${baseUrl}/calculate`,
    },
    results: {
      title: purchaseData ? `Shopping Guilt Score: ${purchaseData.guiltScore}% | Guilt Gauge` : 'Your Shopping Guilt Score | Guilt Gauge',
      description: purchaseData ? 
        `Your ${purchaseData.item} purchase has a guilt score of ${purchaseData.guiltScore}%. Get personalized advice to shop smarter and reduce buyer's remorse.` : 
        'See your personalized shopping guilt score and get tailored advice to make better purchasing decisions without the regret.',
      keywords: 'shopping guilt score, purchase analysis, buyer\'s remorse, emotional spending, retail therapy results, compulsive buying test',
      canonical: `${baseUrl}/results`,
    },
    dashboard: {
      title: 'Shopping Habits Dashboard | Track Your Spending Patterns',
      description: 'View your shopping history, guilt trends, and personalized insights. Understand your shopping personality and develop healthier habits.',
      keywords: 'shopping dashboard, spending tracker, purchase history, shopping patterns, guilt trends, shopping addiction tracking',
      canonical: `${baseUrl}/dashboard`,
    },
    insights: {
      title: 'Your Shopping Psychology & Addiction Patterns | Guilt Gauge',
      description: 'Discover your unique shopping personality and understand the psychology behind your spending habits, emotional triggers, and shopping compulsions.',
      keywords: 'shopping psychology, spending patterns, emotional triggers, shopping personality, shopping analysis, shopping addiction diagnosis',
      canonical: `${baseUrl}/insights`,
    },
    challenges: {
      title: 'Shopping Addiction Recovery Challenges | Overcome Compulsive Buying',
      description: 'Take on personalized challenges designed to help you overcome shopping addiction, reduce impulse purchases, and develop healthier spending habits.',
      keywords: 'shopping challenges, spending habits, impulse buying challenges, no spend challenge, shopaholic recovery, compulsive buying therapy',
      canonical: `${baseUrl}/challenges`,
    },
    goals: {
      title: 'Set Shopping Goals & Recovery Milestones | Guilt Gauge',
      description: 'Create personalized shopping goals and mindful spending budgets. Track your progress and overcome compulsive buying patterns.',
      keywords: 'shopping goals, spending budget, guilt free shopping, mindful budget, shopaholic recovery plan',
      canonical: `${baseUrl}/goals`,
    },
    shop: {
      title: 'Shop Mindfully | Products That Reduce Shopping Addiction',
      description: 'Discover products that align with mindful spending and help recover from shopping addiction. Recommended tools for better financial habits.',
      keywords: 'mindful shopping products, guilt-free purchases, budget tools, shopaholic recovery products',
      canonical: `${baseUrl}/shop`,
    },
    blog: {
      title: 'Why Do We Feel Guilty After Shopping? | Psychology & Solutions',
      description: 'Learn why we feel guilty after shopping, the psychology behind buyer\'s remorse, and 7 proven strategies to overcome shopping guilt and regret.',
      keywords: 'shopping guilt psychology, why feel guilty after shopping, overcome buyer\'s remorse, post-purchase anxiety, shopping regret',
      canonical: `${baseUrl}/blog`,
    },
    'guilt-after-shopping': {
      title: 'Why Do I Feel Guilty After Shopping? 9 Reasons & Solutions',
      description: 'Discover why you feel guilty after shopping, identify your emotional spending triggers, and learn practical strategies to overcome buyer\'s remorse.',
      keywords: 'feel guilty after shopping, guilt after buying clothes, why do I regret shopping, shopping guilt reasons, post purchase anxiety',
      canonical: `${baseUrl}/why-feel-guilty-after-shopping`,
    },
    'shopaholic-test': {
      title: 'Shopping Addiction Test | Are You a Shopaholic? | Free Assessment',
      description: 'Take our free shopping addiction assessment to determine if you have compulsive buying disorder. Get personalized results and recovery strategies.',
      keywords: 'shopping addiction test, shopaholic quiz, compulsive buying assessment, shopping disorder test, am I a shopaholic',
      canonical: `${baseUrl}/shopaholic-test`,
    },
    'shopping-addiction': {
      title: 'Shopping Addiction Recovery | Free Tools & Strategies | Guilt Gauge',
      description: 'Get help for shopping addiction with our free recovery tools. Track habits, identify triggers, and develop healthier spending patterns.',
      keywords: 'shopping addiction recovery, compulsive buying help, shopping addiction self help, shopaholic recovery, stop compulsive shopping',
      canonical: `${baseUrl}/shopping-addiction`,
    },
    'fashion-guilt': {
      title: 'Overcome Fashion Shopping Guilt | Guilt Gauge',
      description: 'Track your fashion purchases and reduce guilt with our free tool. Learn to shop mindfully for clothes and avoid buyer\'s remorse. Explore our shopping addiction resources.',
      keywords: 'fashion shopping guilt, guilt after buying clothes, mindful fashion shopping, buyer\'s remorse clothing, shopping addiction fashion',
      canonical: `${baseUrl}/fashion-shopping-guilt`,
    },
    'tech-guilt': {
      title: 'Tech Shopping Guilt Calculator | Guilt Gauge',
      description: 'Calculate guilt scores for tech purchases and shop smarter. Avoid buyer\'s remorse on gadgets with our free tool. Check out our emotional shopping tracker.',
      keywords: 'tech shopping guilt, buyer\'s remorse tech, mindful tech shopping, guilt after buying electronics, emotional shopping tech',
      canonical: `${baseUrl}/tech-shopping-guilt`,
    },
    'luxury-guilt': {
      title: 'Luxury Shopping Guilt Solutions | Guilt Gauge',
      description: 'Manage guilt from luxury purchases with our free tracker. Get personalized advice for high-end shopping. Learn more about shopping psychology.',
      keywords: 'luxury shopping guilt, buyer\'s remorse luxury, mindful luxury shopping, guilt after luxury purchases, shopping psychology',
      canonical: `${baseUrl}/luxury-shopping-guilt`,
    },
    'home-decor-remorse': {
      title: 'Home Decor Buyer\'s Remorse Help | Guilt Gauge',
      description: 'Overcome home decor buyer\'s remorse with our free tool. Track purchases and shop mindfully for your home. Explore our no-spend challenge.',
      keywords: 'home decor remorse, buyer\'s remorse home decor, mindful home shopping, guilt after home purchases, no spend challenge',
      canonical: `${baseUrl}/home-decor-remorse`,
    },
    'holiday-guilt': {
      title: 'Holiday Shopping Guilt Tracker | Guilt Gauge',
      description: 'Track holiday shopping and reduce guilt with our free tool. Get tips for stress-free festive spending. See our Black Friday remorse solutions.',
      keywords: 'holiday shopping guilt, buyer\'s remorse holiday, mindful holiday shopping, guilt after holiday purchases, black friday shopping',
      canonical: `${baseUrl}/holiday-shopping-guilt`,
    },
    'black-friday-remorse': {
      title: 'Black Friday Buyer\'s Remorse Solutions | Guilt Gauge',
      description: 'Avoid Black Friday buyer\'s remorse with our guilt tracker. Shop deals mindfully and regret-free. Check out our holiday shopping guilt tracker.',
      keywords: 'black friday remorse, buyer\'s remorse black friday, mindful deal shopping, guilt after black friday, holiday shopping guilt',
      canonical: `${baseUrl}/black-friday-remorse`,
    },
    'birthday-splurge': {
      title: 'Birthday Splurge Guilt Calculator | Guilt Gauge',
      description: 'Calculate guilt for birthday splurges with our free tool. Shop for celebrations without regret. Explore our emotional shopping tracker.',
      keywords: 'birthday splurge guilt, buyer\'s remorse birthday, mindful birthday shopping, guilt after birthday purchases, emotional shopping',
      canonical: `${baseUrl}/birthday-splurge`,
    },
    'student-guilt': {
      title: 'Student Shopping Guilt Tracker | Guilt Gauge',
      description: 'Track student shopping and reduce guilt with our free tool. Get budgeting tips for students. See our budget shopping solutions.',
      keywords: 'student shopping guilt, buyer\'s remorse student, mindful student shopping, guilt after student purchases, budget shopping',
      canonical: `${baseUrl}/student-shopping-guilt`,
    },
    'parent-guilt': {
      title: 'Parent Shopping Guilt Solutions | Guilt Gauge',
      description: 'Manage parent shopping guilt with our free tracker. Shop for your family without regret. Explore our shopping psychology insights.',
      keywords: 'parent shopping guilt, buyer\'s remorse parent, mindful family shopping, guilt after family purchases, shopping psychology',
      canonical: `${baseUrl}/parent-shopping-guilt`,
    },
    'retirement-shopping': {
      title: 'Retirement Shopping Guilt Help | Guilt Gauge',
      description: 'Track retirement shopping and avoid guilt with our free tool. Get advice for mindful spending in retirement. Check out our budget shopping tips.',
      keywords: 'retirement shopping guilt, buyer\'s remorse retirement, mindful retirement shopping, guilt after retirement purchases, budget shopping',
      canonical: `${baseUrl}/retirement-shopping`,
    },
    'debt-shopping': {
      title: 'Shopping with Debt Guilt Tracker | Guilt Gauge',
      description: 'Shop mindfully while managing debt with our free tool. Track purchases and reduce guilt. See our shopping guilt vs financial anxiety guide.',
      keywords: 'debt shopping guilt, buyer\'s remorse debt, mindful debt shopping, guilt after debt purchases, financial anxiety',
      canonical: `${baseUrl}/debt-shopping`,
    },
    'budget-shopping': {
      title: 'Budget Shopping Guilt Solutions | Guilt Gauge',
      description: 'Stick to your budget and shop guilt-free with our free tool. Get personalized budgeting tips. Explore our no-spend challenge.',
      keywords: 'budget shopping guilt, buyer\'s remorse budget, mindful budget shopping, guilt after budget purchases, no spend challenge',
      canonical: `${baseUrl}/budget-shopping`,
    },
    'post-payday': {
      title: 'Post-Payday Shopping Guilt Calculator | Guilt Gauge',
      description: 'Track post-payday shopping and avoid guilt with our free tool. Get tips for mindful spending after payday. Check out our impulse buying solutions.',
      keywords: 'post payday shopping guilt, buyer\'s remorse payday, mindful payday shopping, guilt after payday purchases, impulse buying',
      canonical: `${baseUrl}/post-payday-shopping`,
    },
    'emotional-shopping': {
      title: 'Emotional Shopping Tracker | Guilt Gauge',
      description: 'Track emotional purchases and understand triggers with our free tool. Shop mindfully and reduce guilt. See our retail therapy vs addiction guide.',
      keywords: 'emotional shopping guilt, buyer\'s remorse emotional, mindful emotional shopping, guilt after emotional purchases, retail therapy',
      canonical: `${baseUrl}/emotional-shopping-tracker`,
    },
    'impulse-buying': {
      title: 'Impulse Buying Guilt Solutions | Guilt Gauge',
      description: 'Overcome impulse buying with our free guilt tracker. Get personalized tips for mindful shopping. Explore our FOMO shopping guide.',
      keywords: 'impulse buying guilt, buyer\'s remorse impulse, mindful impulse shopping, guilt after impulse purchases, fomo shopping',
      canonical: `${baseUrl}/impulse-buying`,
    },
    'fomo-shopping': {
      title: 'FOMO Shopping Guilt Calculator | Guilt Gauge',
      description: 'Calculate guilt for FOMO-driven purchases with our free tool. Shop mindfully and avoid regret. Check out our impulse buying solutions.',
      keywords: 'fomo shopping guilt, buyer\'s remorse fomo, mindful fomo shopping, guilt after fomo purchases, impulse buying',
      canonical: `${baseUrl}/fomo-shopping`,
    },
    'late-night-shopping': {
      title: 'Late Night Shopping Guilt Tracker | Guilt Gauge',
      description: 'Track late-night shopping and reduce guilt with our free tool. Get tips for mindful purchasing decisions. See our emotional shopping tracker.',
      keywords: 'late night shopping guilt, buyer\'s remorse late night, mindful late night shopping, guilt after late night purchases, emotional shopping',
      canonical: `${baseUrl}/late-night-shopping`,
    },
    'no-spend-challenge': {
      title: 'No-Spend Challenge | Guilt Gauge',
      description: 'Join our no-spend challenge to reduce shopping guilt and build mindful spending habits. Get free tools and tips. Explore our shopping detox program.',
      keywords: 'no spend challenge, mindful spending challenge, shopping guilt challenge, guilt free shopping, shopping detox',
      canonical: `${baseUrl}/no-spend-challenge`,
    },
    'shopping-detox': {
      title: 'Shopping Detox Program | Guilt Gauge',
      description: 'Start a shopping detox to overcome guilt and compulsive buying with our free tool. Get personalized recovery tips. See our no-spend challenge.',
      keywords: 'shopping detox, compulsive buying detox, shopping guilt detox, mindful shopping program, no spend challenge',
      canonical: `${baseUrl}/shopping-detox`,
    },
    'addiction-stories': {
      title: 'Shopping Addiction Recovery Stories | Guilt Gauge',
      description: 'Read inspiring shopping addiction recovery stories and learn strategies to shop mindfully. Get free tools for recovery. Explore our shopping addiction test.',
      keywords: 'shopping addiction stories, compulsive buying recovery, shopping guilt recovery, mindful shopping stories, shopping addiction test',
      canonical: `${baseUrl}/shopping-addiction-stories`,
    },
    'guilt-vs-anxiety': {
      title: 'Shopping Guilt vs Financial Anxiety | Guilt Gauge',
      description: 'Understand shopping guilt vs financial anxiety with our free tools. Manage both and shop smarter. Check out our debt shopping tracker.',
      keywords: 'shopping guilt vs anxiety, financial anxiety shopping, buyer\'s remorse anxiety, mindful shopping anxiety, debt shopping',
      canonical: `${baseUrl}/shopping-guilt-vs-anxiety`,
    },
    'retail-therapy': {
      title: 'Retail Therapy vs Shopping Addiction | Guilt Gauge',
      description: 'Learn the difference between retail therapy and shopping addiction with our free tools. Get tips for healthy spending. See our emotional shopping tracker.',
      keywords: 'retail therapy vs addiction, shopping addiction guide, buyer\'s remorse retail therapy, mindful retail therapy, emotional shopping',
      canonical: `${baseUrl}/retail-therapy-vs-addiction`,
    },
    'remorse-timeline': {
      title: 'Buyer\'s Remorse Timeline | Guilt Gauge',
      description: 'Explore the buyer\'s remorse timeline and learn how to shop without regret with our free tool. Get personalized advice. Check out our shopping psychology insights.',
      keywords: 'buyer\'s remorse timeline, shopping guilt timeline, mindful shopping guide, guilt after purchases, shopping psychology',
      canonical: `${baseUrl}/buyers-remorse-timeline`,
    },
    'shareable-infographics': {
      title: "Shareable Shopping Psychology Infographics | Guilt Gauge",
      description: "Download and share beautiful infographics about shopping psychology, mindful spending tips, and overcoming shopping guilt. Perfect for Instagram!",
      keywords: "shopping infographics, shopping psychology, mindful spending, social media graphics, instagram shopping tips",
      canonical: `${baseUrl}/shareable-infographics`,
    },
    "buy-now-pay-later": {
      title: "Buy Now, Pay Later: Hidden Costs & Risks | Guilt Gauge",
      description: "Understand the true cost of Buy Now, Pay Later services like Affirm, Klarna, and Afterpay. Learn about hidden fees and psychological tactics.",
      keywords: "buy now pay later, BNPL, Affirm, Klarna, Afterpay, pay later risks, BNPL fees, buy now pay later debt",
      canonical: `${baseUrl}/buy-now-pay-later`,
    },
  };
  
  // Merge default meta with view-specific meta
  const currentMeta = { ...defaultMeta, ...(viewMeta[currentView] || {}) };
  
  // Calculate additional meta based on user profile if available
  if (userProfile && (currentView === 'dashboard' || currentView === 'insights')) {
    const personalityType = userProfile.personalityType || 'Shopper';
    currentMeta.title = `Your ${personalityType} Shopping Dashboard | Guilt Gauge`;
    currentMeta.description = `Personalized insights for your ${personalityType.toLowerCase()} shopping style. Track your spending patterns and overcome compulsive habits.`;
  }
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{currentMeta.title}</title>
      <meta name="description" content={currentMeta.description} />
      <meta name="keywords" content={currentMeta.keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={getCanonicalUrl(currentView)} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={currentMeta.ogType} />
      <meta property="og:url" content={currentMeta.canonical} />
      <meta property="og:title" content={currentMeta.title} />
      <meta property="og:description" content={currentMeta.description} />
      <meta property="og:image" content={currentMeta.ogImage} />
      
      {/* Twitter */}
      <meta property="twitter:card" content={currentMeta.twitterCard} />
      <meta property="twitter:url" content={currentMeta.canonical} />
      <meta property="twitter:title" content={currentMeta.title} />
      <meta property="twitter:description" content={currentMeta.description} />
      <meta property="twitter:image" content={currentMeta.ogImage} />
      
      {/* Additional Meta Tags for SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Guilt Gauge" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    </Helmet>
  );
};

export default EnhancedSEOMeta;
